<script lang="ts">
  import { createEventDispatcher, getContext } from 'svelte';
  import { link } from 'svelte-spa-router';
  import type { MenuItem } from '$stores/ui.js';

  export let item: MenuItem;
  export let depth: number = 0;
  // Only meaningful at depth 0 — controlled by Menubar via openGroup state.
  export let isOpen: boolean = false;

  const dispatch = createEventDispatcher<{ open: void; close: void }>();
  const closeAll = getContext<() => void>('closeAll');

  // depth 0: parent-controlled; depth > 0: self-managed
  let selfOpen = false;
  $: open = depth === 0 ? isOpen : selfOpen;
  $: hasChildren = !!(item.children?.length);

  function handleMouseEnter() {
    if (!hasChildren) return;
    if (depth === 0) dispatch('open');
    else selfOpen = true;
  }

  function handleMouseLeave() {
    if (!hasChildren) return;
    if (depth === 0) dispatch('close');
    else selfOpen = false;
  }

  function toggle(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (depth === 0) {
      dispatch(open ? 'close' : 'open');
    } else {
      selfOpen = !selfOpen;
    }
  }

  function handleLabelClick(e: MouseEvent) {
    if (item.path) {
      closeAll?.();
    } else {
      toggle(e);
    }
  }
</script>

<div
  class="wrap"
  class:is-top={depth === 0}
  role="none"
  on:mouseenter={handleMouseEnter}
  on:mouseleave={handleMouseLeave}
>
  <div class="row" class:has-split={hasChildren}>
    {#if item.path}
      <a href={item.path} use:link class="label" on:click={handleLabelClick}>{item.label}</a>
    {:else}
      <button class="label" on:click={handleLabelClick}>{item.label}</button>
    {/if}

    {#if hasChildren}
      <button
        class="chevron"
        on:click={toggle}
        aria-expanded={open}
        aria-label={__STRINGS__.nav.toggleSubmenu}
      >
        <svg class:open viewBox="0 0 10 6" width="8" height="5" aria-hidden="true">
          <path d="M0 0l5 6 5-6z" fill="currentColor" />
        </svg>
      </button>
    {/if}
  </div>

  {#if open && hasChildren}
    <div class="submenu" class:is-dropdown={depth === 0} class:is-flyout={depth > 0}>
      {#each item.children ?? [] as child}
        <svelte:self item={child} depth={depth + 1} />
      {/each}
    </div>
  {/if}
</div>

<style>
  /* ── Wrappers ── */
  .wrap.is-top {
    position: relative;
  }

  /* Non-top wrappers are positioned so their flyout can anchor to them */
  .wrap:not(.is-top) {
    position: relative;
  }

  /* ── Row: label + optional chevron ── */
  .row {
    display: flex;
    align-items: stretch;
    border-radius: 4px;
    overflow: hidden;
  }

  /* Top-level items get a visible button background so they read as discrete
     items — this also makes the chevron's internal separator look contained
     within the item rather than between adjacent items. */
  .wrap.is-top .row {
    background: rgba(255, 255, 255, 0.09);
  }

  /* Dropdown / flyout rows are full-width; no rounded corners needed */
  .wrap:not(.is-top) .row {
    border-radius: 0;
  }

  /* ── Label (<a> and <button> share the same look) ── */
  .label {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 0.5rem 1rem;
    color: var(--color-menubar-text, #fff);
    text-decoration: none;
    opacity: 0.85;
    transition: opacity 0.15s, background 0.15s;
    font-family: inherit;
    font-size: inherit;
    background: none;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    text-align: left;
  }

  .has-split .label {
    padding-right: 0.5rem;
  }

  .label:hover,
  .label:focus-visible {
    opacity: 1;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
    outline: none;
  }

  /* ── Chevron button ── */
  .chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.6rem;
    background: none;
    border: none;
    border-left: 1px solid rgba(255, 255, 255, 0.18);
    color: var(--color-menubar-text, #fff);
    opacity: 0.7;
    cursor: pointer;
    transition: opacity 0.15s, background 0.15s;
  }

  .chevron:hover,
  .chevron:focus-visible {
    opacity: 1;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
    outline: none;
  }

  svg {
    display: block;
    transition: transform 0.15s;
  }

  /* Depth-0 chevron rotates up/down for a below-opening dropdown */
  .wrap.is-top svg.open {
    transform: rotate(180deg);
  }

  /* Depth > 0 chevron points right by default (indicates sideways flyout),
     rotates to point left when the flyout is open */
  .wrap:not(.is-top) svg {
    transform: rotate(-90deg);
  }

  .wrap:not(.is-top) svg.open {
    transform: rotate(90deg);
  }

  /* ── First-level dropdown panel (opens below) ── */
  .submenu.is-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 220px;
    background: var(--color-menubar-bg, #1a1a2e);
    border-radius: 0 0 4px 4px;
    padding: 4px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    z-index: 200;
  }

  /* ── Nested flyout panel (opens to the right) ── */
  .submenu.is-flyout {
    position: absolute;
    left: 100%;
    top: 0;
    min-width: 200px;
    background: var(--color-menubar-bg, #1a1a2e);
    border-radius: 4px;
    padding: 4px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    z-index: 201;
  }
</style>
